//////LIBRARY//////
import React, { useEffect, useState, Fragment, useMemo } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { bindActionCreators } from 'redux'
import moment from 'moment'
import { cloneDeep, isEmpty, isUndefined, map, isEqual, isNull } from 'lodash'
//////COMPONENTS//////
import Theme from '../../theme/theme'
import { DatePickerFilterLabel } from '../../theme/GlobalCss'
import Table from '../../components/Table'
import DropdownCheckbox from '../../components/DropdownCheckbox'
import UseDatePicker from '../../hooks/UseDatePicker'
import UseCheckAll from '../../hooks/UseCheckAll'
import { selectIdsUser } from '../../store/selectors/user'
import { marketingActionCreators, columnsActionCreators, tableActionCreators } from '../../store/actions'
import userPrivileges from '../../constants/PrivilegesList'
import urlExport from '../../constants/UrlExport'
import TableTitle from '../../components/Table/components/TableTitle'
//////ASSETS//////
import Calendar from '../../assets/icones/global/calendar.svg'

//////STYLED//////
const UsersManagerWrapper = styled.div`
	position: relative;
`
const DatePickerTransactionIcon = styled.div`
	border-radius: 4px;
	background-color: ${(props) => props.theme.colors.primary};
	height: 32px;
	width: 32px;
	padding: 6px;
	box-sizing: border-box;
	cursor: pointer;
	background-image: url(${Calendar});
	background-repeat: no-repeat;
	background-size: 20px 20px;
	background-position: center;
	:hover {
		background-color: ${(props) => props.theme.colors.hoverPrimary};
	}
	transition: background-color 0.1s;
`
//////STYLED//////

const MarketingManager = () => {
	const { user, translations, marketing, marketingPaging, marketingHeader, isExported, currentColumn, currentDir } = useSelector(
		(state) => ({
			translations: state.languages.translations,
			user: state.user,
			userIds: selectIdsUser(state),
			userKioskPerimeter: state.user.identity.kioskSelected,
			marketing: state.marketing,
			marketingPaging: state.marketing.paging,
			marketingHeader: state.marketing.header,
			isExported: state.exportData.isExported,
			currentColumn: state.columns.currentColumn,
			currentDir: state.columns.currentDir,
		}),
		shallowEqual
	)

	const actionsMarketing = bindActionCreators(marketingActionCreators, useDispatch())
	const actionsColumns = bindActionCreators(columnsActionCreators, useDispatch())
	const actionsTable = bindActionCreators(tableActionCreators, useDispatch())
	// Filters
	const [prevFilter, setPrevFilter] = useState({})

	const [filters, setFilters, handleCheckElements] = UseCheckAll(prevFilter)
	// Data
	const [paging, setPaging] = useState({ page: '0', size: '250', sort: 'saleOrder.allowComData', nameDir: 'desc' })

	// Search bar
	const [searchValue, setSearchValue] = useState()
	const [
		dateTransactionShowModal,
		dateTransactionOpenDateModalHandler,
		dateTransactionRenderModal,
		dateTransactionStateDate,
		setStartDate,
		setEndDate,
	] = UseDatePicker({
		defaultDateStart: moment().startOf('day').subtract(1, 'months').toDate(),
		defaultDateEnd: moment().hours(23).minutes(59).toDate(),
	})

	// Columns
	const [columns, setColumns] = useState([])

	//////////////////////////////////// HANDLER /////////////////////////////////////////////////////////

	const handleFilterSend = () => {
		return {
			withFilters: true,
			...filters,
			datesRange: {
				dateStart: dateTransactionStateDate.date.startDate,
				dateEnd: dateTransactionStateDate.date.endDate,
			},
			userKioskScope: {
				kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
			},
		}
	}

	const handlePagingSalesHistory = (paging) => {
		const pg = {
			...paging,
			...{
				sort: !isUndefined(currentColumn.value) ? currentColumn.value : 'saleOrder.date',
				nameDir: !isUndefined(currentDir.value) ? currentDir.value : 'desc',
			},
		}
		setPaging(pg)
		actionsMarketing.postMarketing(handleFilterSend(), pg)
	}

	const handleSubmitFilters = () => {
		if (isEqual(filters, prevFilter)) return // guard to call if no change
		setPrevFilter(filters)
		// Force reset paging at zero, in case when filters are preventing any pagination with current size
		let pg = cloneDeep(paging)
		if (isUndefined(pg)) {
			pg = { page: '0', size: '250', sort: 'saleOrder.date', nameDir: 'desc' }
		} else {
			pg.page = '0'
		}
		setPaging(pg)

		actionsMarketing.postMarketing(handleFilterSend(), pg)
	}

	/////////////////////////////// CONFIG //////////////////////////////////////

	useEffect(() => {
		setColumns([
			{
				columnReferential: 'marketing_marketingCommunication',
				dataHook: 'allowMarketing',
				headerName: translations['page__marketing__admin__marketingTable_tableHeader_businessCommunication'],
				sortable: true,
				isAuthorize: true,
				component: (props) => (props?.row?.allowMarketing ? translations['common__text__yes'] : translations['common__text__no']),
				sortableId: 'saleOrder.allowComData',
			},
			{
				columnReferential: 'marketing_email',
				dataHook: 'email',
				headerName: translations['page__marketing__admin__marketingTable_tableHeader_mail'],
				sortable: true,
				isAuthorize: true,
				sortableId: 'saleOrder.customerEmail',
			},
			{
				columnReferential: 'marketing_transactionDate',
				dataHook: 'transactionDate',
				headerName: translations['page__marketing__admin__marketingTable_tableHeader_transactionDate'],
				sortable: true,
				isAuthorize: true,
				component: (props) => moment(props.row.transactionDate).format('DD/MM/YYYY HH:mm'),
				sortableId: 'saleOrder.date',
			},

			{
				columnReferential: 'marketing_transactionType',
				dataHook: 'transactionType.translationKey',
				headerName: translations['page__marketing__admin__marketingTable_tableHeader_transactionType'],
				sortable: true,
				isAuthorize: true,
				toTranslate: true,
				sortableId: 'saleOrder.saleOrderType.label',
			},
			{
				columnReferential: 'marketing_itemLabel',
				dataHook: 'itemLabel',
				headerName: translations['page__marketing__admin__marketingTable_tableHeader_itemLabel'],
				sortable: true,
				isAuthorize: true,
				sortableId: 'itemKiosk.itemDistributor.item.labelToDisplay',
			},

			{
				columnReferential: 'marketing_lastExportDate',
				dataHook: 'lastExport',
				headerName: translations['page__marketing__admin__marketingTable_tableHeader_lastExportDate'],
				sortable: true,
				isAuthorize: true,
				component: (props) => (isNull(props?.row?.lastExport) ? '-' : moment(props.row.lastExport).format('DD/MM/YYYY HH:mm')),
				sortableId: 'saleOrder.comLastExportDate',
			},
		])
	}, [translations])

	// Used for columns displaying (redux stored)
	const tableReferential = {
		idPageTable: 11,
		labelPageTable: 'marketing',
	}

	const [configTableTitle, setConfigTableTitle] = useState({
		actions: {
			dateLists: [
				() => (
					<Fragment>
						<label style={{ color: Theme.colors.table.body.text, marginRight: '10px', lineHeight: '32px' }}>
							{translations['page__global__admin__header__transactionDate']} :
						</label>
						<DatePickerTransactionIcon onClick={dateTransactionOpenDateModalHandler} />
						<DatePickerFilterLabel>
							<span>
								{translations['page__global__admin__header_datePicker_from']}{' '}
								{isEqual(dateTransactionStateDate.date.startDate, {})
									? moment(new Date()).subtract(1, 'months').format('DD/MM/YYYY HH:mm')
									: moment(dateTransactionStateDate.date.startDate).format('DD/MM/YYYY HH:mm')}
							</span>
							<span>
								{translations['page__global__admin__header_datePicker_to']}{' '}
								{isEqual(dateTransactionStateDate.date.endDate, {})
									? moment(new Date()).format('DD/MM/YYYY HH:mm')
									: moment(dateTransactionStateDate.date.endDate).format('DD/MM/YYYY HH:mm')}
							</span>
						</DatePickerFilterLabel>
					</Fragment>
				),
			],
		},
		paging: {
			fnSendTo: (paging) => handlePagingSalesHistory(paging),
		},
	})
	const [configTable, setConfigTable] = useState({})

	////////////////////////////////USE EFFECT/////////////////////////////////////////
	useEffect(() => {
		let state_date = {
			startDate: moment().startOf('day').subtract(1, 'months').toDate(),
			endDate: moment().hours(23).minutes(59).toDate(),
		}

		if (isUndefined(user) || isEmpty(translations)) return
		if (isEmpty(filters)) {
			const filtersCp = {
				withFilters: false,
				userKioskScope: {
					kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
				},
				filtersSelection: null,
				datesRange: {
					dateStart: state_date.startDate,
					dateEnd: state_date.endDate,
				},
			}

			actionsMarketing.postMarketing(filtersCp)
			actionsColumns.setColumns([])
		}
	}, [translations])
	useEffect(() => {
		if (!isExported) {
			setFilters(marketingHeader)
			setPrevFilter(marketingHeader)
		}
	}, [translations, marketingHeader])

	useEffect(() => {
		const cfg = cloneDeep(configTableTitle)
		cfg.paging = {
			totalElements: marketingPaging.totalElements,
			totalPages: marketingPaging.totalPages,
			fnSendTo: (paging) => handlePagingSalesHistory(paging),
		}
		setConfigTableTitle(cfg)
	}, [marketing.items])

	useEffect(() => {
		let cfg = cloneDeep(configTableTitle)
		cfg.name = translations['page__marketing__title']
		cfg.paging = {
			totalElements: marketingPaging.totalElements,
			totalPages: marketingPaging.totalPages,
			fnSendTo: (paging) => handlePagingSalesHistory(paging),
		}
		cfg.actions.rest = [
			() => (
				<DropdownCheckbox
					title={translations['page__marketing__admin__filters_businessCommunication']}
					data={filters?.filtersSelection?.allowMarketing?.filterElements || []}
					translations={translations}
					toTranslate
					paperTopPosition={'40'}
					dropColor={'blueColor'}
					selectActionsHandler={handleCheckElements}
					submitOnClose={handleSubmitFilters}
					target={'allowMarketing'}
				/>
			),
		]
		cfg.actions.dateLists = [
			() => (
				<Fragment>
					<label style={{ color: Theme.colors.table.body.text, marginRight: '10px', lineHeight: '32px' }}>
						{translations['page__global__admin__header__transactionDate']} :
					</label>
					<DatePickerTransactionIcon onClick={dateTransactionOpenDateModalHandler} />
					<DatePickerFilterLabel>
						<span>
							{translations['page__global__admin__header_datePicker_from']}{' '}
							{moment(dateTransactionStateDate.date.startDate).format('DD/MM/YYYY HH:mm')}
						</span>
						<span>
							{translations['page__global__admin__header_datePicker_to']}{' '}
							{moment(dateTransactionStateDate.date.endDate).format('DD/MM/YYYY HH:mm')}
						</span>
					</DatePickerFilterLabel>
				</Fragment>
			),
		]

		setConfigTableTitle(cfg)
	}, [translations, filters, dateTransactionStateDate])

	useEffect(() => {
		const cfgCp = cloneDeep(configTable)
		cfgCp.export = {
			privilege: userPrivileges.export[19],
			url: urlExport[17],
			bd: {
				filtersSelection: filters.filtersSelection,
				searchFieldByItem: searchValue,
				datesRange: {
					dateStart: dateTransactionStateDate.date.startDate,
					dateEnd: dateTransactionStateDate.date.endDate,
				},
				withFilters: true,
				userKioskScope: {
					kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
				},
			},
		}

		setConfigTable(cfgCp)
	}, [translations, filters])

	useEffect(() => {
		if (dateTransactionStateDate.showModal) {
			actionsMarketing.postMarketing(handleFilterSend(), paging)
		}
	}, [dateTransactionShowModal])
	useEffect(() => {
		if (isExported) {
			actionsMarketing.postMarketing(
				{
					withFilters: true,
					...filters,
					datesRange: {
						dateStart: dateTransactionStateDate.date.startDate,
						dateEnd: dateTransactionStateDate.date.endDate,
					},
					userKioskScope: {
						kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
					},
				},
				paging
			)
		}
	}, [isExported])

	useEffect(() => {
		return function cleanUp() {
			actionsMarketing.resetMarketing()

			actionsTable.resetPaggingConfig()
		}
	}, [])

	///////////////////////////////// RENDER ///////////////////////////////////////

	const TableRender = useMemo(() => {
		return (
			<Table
				inputColumns={columns}
				inputData={marketing.items}
				inputConfig={configTable}
				isLoading={marketing.isLoading}
				currentPaging={paging}
				tableReferential={tableReferential}
				actionSort={actionsMarketing.postMarketing}
			/>
		)
	}, [marketing, columns, configTable, translations, paging])

	return (
		<Fragment>
			{dateTransactionRenderModal()}
			<UsersManagerWrapper>
				{!isUndefined(translations) && !isUndefined(configTableTitle) && (
					<TableTitle inputConfig={configTableTitle} inputColumns={columns} tableReferential={tableReferential} />
				)}
				{!isUndefined(translations) && !isUndefined(configTable) && TableRender}
			</UsersManagerWrapper>
		</Fragment>
	)
}

MarketingManager.propTypes = {}

MarketingManager.defaultPropTypes = {}

export default MarketingManager
